import React, { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useSearchParams } from 'react-router-dom';

declare global {
  interface Window {
    fbq?: (action: string, event: string, params?: { value?: number; currency?: string }) => void;
  }
}

/**
 * Stripe Checkout success page. Fires Meta Pixel Purchase event once when
 * session_id is present and backend confirms payment_status === 'paid'.
 */
const SuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) return;
    const firedKey = `meta_purchase_${sessionId}`;
    if (sessionStorage.getItem(firedKey)) return;

    const apiBase = import.meta.env.VITE_MINDRIGHT_API_ENDPOINT || '';
    const url = `${apiBase.replace(/\/$/, '')}/api/v1/stripe/session?session_id=${encodeURIComponent(sessionId)}`;

    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) return;
        const data = await res.json();
        if (typeof data.value !== 'number' || !data.currency) return;
        if (window.fbq) {
          window.fbq('track', 'Purchase', {
            value: data.value,
            currency: data.currency,
          });
        }
        sessionStorage.setItem(firedKey, '1');
      } catch {
        // Silently ignore so the page still shows success
      }
    })();
  }, [sessionId]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        px: 2,
      }}
    >
      <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
      <Typography variant="h5" component="h1" gutterBottom>
        Payment successful
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Thank you. Your payment has been confirmed.
      </Typography>
    </Box>
  );
};

export default SuccessPage;
